import { getErrorJson } from '@mm/utils';
import { head } from 'lodash';
import { Dispatch } from 'redux';
import ConnectedPageController from '../controllers/ConnectedPageController';
import connectedPageHandler from '../errorHandlers/connected-page-handler';
import { AvailablePage, BasicPage } from '../interfaces/connected-page';
import { UserRoleMappedToBECode } from '../model/CompanyUsersModel';
import { activeFacebookPageSelector } from '../selectors/facebookPageSelector';
import {
  hasConnectedRealFacebookPageSelector,
  isFacebookConnectedSelector,
} from '../selectors/userSelector';
import { selectActiveCompanyId } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import audiencesCRUDActions from './AudiencesCRUDActions';
import chatbotCRUDActions from './ChatbotCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import facebookErrorCRUDActions from './FacebookErrorCRUDActions';
import sessionCRUDActions from './SessionCRUDActions';
import subscriptionCRUDActions from './SubscriptionCRUDActions';
import adAudienceCRUDActions from './AdAudienceCRUDActions';

export type ExtraBasicListDataParams = {
  withSubscription?: boolean;
  withFacebookErrors?: boolean;
  withDowngradePending?: boolean;
  withMessagesStatistic?: boolean;
  withPastSubscriptions?: boolean;
  withContactsCount?: boolean;
  searchQuery?: string;
  showInstagramAccounts?: boolean;
};

const DEFAULT_EXTRA_DATA_PARAMS: ExtraBasicListDataParams = {
  withSubscription: false,
  withFacebookErrors: false,
  withDowngradePending: false,
  withMessagesStatistic: true,
};

export const INVITED_USER_AUTH_ERROR = 1021;

// invited non facebook auth user attempts to fetch data for FB page
export const isInvitedUserAuthError = async (e: any) => {
  if (e instanceof Response) {
    const json = await getErrorJson(e);
    return json?.error_code === INVITED_USER_AUTH_ERROR;
  }
};

class ConnectedPageCRUDActions extends CrudActionContainer {
  setPermission: string;
  adPublished: string;
  retrieveAvailablePagesAction: string;
  resetAvailablePagesAction: string;
  retrievedAvailablePagesAction: string;
  retrieveBasicListAction: string;
  retrievedBasicListAction: string;
  refreshAccessTokenAction: string;
  allowPageForUsersAction: string;
  retrievedPaidPagesAction: string;
  fetchConnectedPageAction: string;
  nullifyConnectedPageAction: string;
  retrieveSubscriptionMessagingStatusAction: string;
  retrievedSubscriptionMessagingStatusAction: string;
  fetchAccessiblePagesByRoleAction: string;

  constructor() {
    super('CONNECTED_PAGE');
    this.adPublished = this.makeActionKey('AD_PUBSLISHED');
    this.setPermission = this.makeActionKey('SET_PERMISSION');
    this.retrieveAvailablePagesAction = this.makeActionKey(
      'RETRIEVE_AVAILABLE_PAGES',
    );
    this.resetAvailablePagesAction = this.makeActionKey(
      'RESET_AVAILABLE_PAGES',
    );
    this.retrievedAvailablePagesAction = this.makeActionKey(
      'RETRIEVED_AVAILABLE_PAGES',
    );
    this.retrieveBasicListAction = this.makeActionKey('RETRIEVE_BASIC_LIST');
    this.retrievedBasicListAction = this.makeActionKey('RETRIEVED_BASIC_LIST');
    this.refreshAccessTokenAction = this.makeActionKey('REFRESH_ACCESS_TOKEN');
    this.allowPageForUsersAction = this.makeActionKey('ALLOW_PAGE_FOR_USERS');
    this.retrievedPaidPagesAction = this.makeActionKey('RETRIEVED_PAID_PAGES');
    this.fetchConnectedPageAction = this.makeActionKey('FETCH_CONNECTED_PAGE');
    this.nullifyConnectedPageAction = this.makeActionKey(
      'NULLIFY_CONNECTED_PAGE',
    );
    this.retrieveSubscriptionMessagingStatusAction = this.makeActionKey(
      'RETRIEVE_SUBSCRIPTION_MESSAGING_STATUS',
    );
    this.retrievedSubscriptionMessagingStatusAction = this.makeActionKey(
      'RETRIEVED_SUBSCRIPTION_MESSAGING_STATUS',
    );
    this.fetchAccessiblePagesByRoleAction = this.makeActionKey(
      'FETCH_ACCESSIBLE_PAGES_BY_ROLE',
    );
  }

  clearErrorStatus = (dispatch: Dispatch<StoreState>) => {
    dispatch({
      type: this.setPermission,
      hasPermission: true,
    });
  };

  create(
    entity,
    companyId?: number,
    igAccountId?: string,
    handleErrors: boolean = true,
    waitForRetrieve: boolean = false,
    ...retrieveOptions: Parameters<typeof ConnectedPageController.getAll>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      this.clearErrorStatus(dispatch);
      try {
        const response = await ConnectedPageController.postConnectedPage(
          entity,
          companyId,
          igAccountId,
          false,
        );
        if (waitForRetrieve) {
          await dispatch(this.retrieveAll(...retrieveOptions));
        } else {
          dispatch(this.retrieveAll(...retrieveOptions));
        }
        if (entity.botId) {
          dispatch(chatbotCRUDActions.retrieve(entity.botId));
        }
        return response;
      } catch (e) {
        if (handleErrors) {
          connectedPageHandler(dispatch)(e);
        } else {
          throw e;
        }
      }
    };
  }

  createBusinessPage(
    ...params: Parameters<typeof ConnectedPageController.createBusinessPage>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      this.clearErrorStatus(dispatch);
      try {
        const res = await ConnectedPageController.createBusinessPage(...params);
        dispatch(this.retrieveAll());
        return res;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  retrieveAll(...params: Parameters<typeof ConnectedPageController.getAll>) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      this.clearErrorStatus(dispatch);
      try {
        if (!params[0]?.skipAvailable) {
          dispatch(this.retrieveAvailablePages(25));
        }
        const response = await ConnectedPageController.getAll(...params);

        // We usually expect an array but send a object here. Fix Later.
        // @ts-ignore
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        await connectedPageHandler(dispatch)(e);
      }
    };
  }

  retrieveAllByCompany(
    companyId: number,
    pageNumber = 0,
    withSubscription = false,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      this.clearErrorStatus(dispatch);
      try {
        dispatch(this.retrieveAvailablePages(25));
        const response = await ConnectedPageController.getAllByCompany({
          companyId,
          pageNumber,
          withSubscription,
        });
        // We usually expect an array but send a object here. Fix Later.
        // @ts-ignore
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  // Basic list has { id, name } and is not paginated
  retrieveBasicListByCompany(
    companyId: number,
    extraDataParams: ExtraBasicListDataParams = {},
    skipBusy = false,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<BasicPage[] | undefined> => {
      const {
        withFacebookErrors,
        withSubscription,
        withDowngradePending,
        withPastSubscriptions,
        withContactsCount,
        searchQuery,
        showInstagramAccounts,
      } = Object.assign({}, DEFAULT_EXTRA_DATA_PARAMS, extraDataParams);

      if (!skipBusy) {
        dispatch(this.busy(this.retrieveBasicListAction));
      }

      this.clearErrorStatus(dispatch);
      try {
        const { facebookPages } =
          await ConnectedPageController.getAllPageBasicListByCompany({
            companyId,
            withSubscription,
            withFacebookErrors,
            withDowngradePending,
            withPastSubscriptions,
            withContactsCount,
            withMessagesStatistic: true,
            showInstagramAccounts,
            searchQuery,
          });
        dispatch({
          type: this.retrievedBasicListAction,
          basicPagesList: facebookPages,
        });
        return facebookPages;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  retrieveAvailablePages(
    limit: number = 500,
    next: string = '',
    search: string = '',
    instagram: boolean = false,
    preventReset: boolean = false,
    bypassCache: boolean = false,
  ) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const state = getState();
      const isFacebookConnected = isFacebookConnectedSelector(state);

      if (!isFacebookConnected) {
        return;
      }

      dispatch(this.busy(this.retrieveAvailablePagesAction));
      this.clearErrorStatus(dispatch);
      if (!next && !preventReset) {
        dispatch({ type: this.resetAvailablePagesAction });
      }
      try {
        const availablePages = await ConnectedPageController.getAvailablePages({
          limit,
          next,
          search,
          instagram,
          bypassCache,
        });
        dispatch({
          type: this.retrievedAvailablePagesAction,
          availablePages,
        });
        return availablePages;
      } catch (e) {
        await connectedPageHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  clearAvailablePages() {
    return async (dispatch: Dispatch<StoreState>) =>
      dispatch({ type: this.resetAvailablePagesAction });
  }

  addPagesToBackend({
    pages,
    companyId,
    extraDataParams = {},
    throwException,
    standaloneToken,
    destinationPageId,
    igAccountId,
  }: {
    pages: AvailablePage[];
    companyId: number;
    extraDataParams: ExtraBasicListDataParams;
    throwException?: boolean;
    standaloneToken?: string;
    destinationPageId?: number;
    igAccountId?: string;
  }) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));

      try {
        const response = await Promise.all(
          pages.map(page =>
            ConnectedPageController.postConnectedPage(
              page,
              companyId,
              igAccountId,
              false,
              undefined,
              standaloneToken,
              destinationPageId,
            ),
          ),
        );

        // HACK company id from argument might be mock company
        // back-end might perform company patch so response is our source of truth now.
        // don't ask me why... please push BE to update this design
        const {
          facebookPage: { companyId: responseCompanyId } = {
            companyId: undefined,
          },
        } = head(response.filter(Boolean)) || {};
        const companyIdToUse = responseCompanyId || companyId;

        if (response.some(res => res.success)) {
          dispatch(sessionCRUDActions.update({ needsToConnectPage: false }));
        }

        dispatch(this.retrieveAllByCompany(companyIdToUse, 1));
        dispatch(
          this.retrieveBasicListByCompany(companyIdToUse, extraDataParams),
        );
        return response;
      } catch (e) {
        if (throwException) {
          dispatch(this.unbusy());
          throw e;
        }
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  allowPageForUsers(
    pageId: number,
    allowedUserIds: number[],
    allowedAgentsIds: number[],
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        await ConnectedPageController.allowPageForUsers({
          pageId,
          allowedUserIds,
          allowedAgentsIds,
        });
        dispatch(this.unbusy());
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  moveToCompany(pageId: number, companyId: number, fromCompanyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        await ConnectedPageController.moveToCompany(
          pageId,
          companyId,
          fromCompanyId,
        );
        dispatch(this.unbusy());
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  update(entity) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      this.clearErrorStatus(dispatch);
      try {
        const response = await ConnectedPageController.put(entity);
        dispatch(this.retrieveAll());
        if (response.botId) {
          dispatch(chatbotCRUDActions.retrieve(response.botId));
        }

        dispatch({
          type: this.fetchConnectedPageAction,
          page: response,
        });
        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  delete(
    entity,
    companyId: number | undefined,
    pageNumber: number | undefined,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      this.clearErrorStatus(dispatch);
      try {
        const response = await ConnectedPageController.deleteConnectedPage(
          entity.id,
        );
        if (companyId && pageNumber) {
          dispatch({ type: this.nullifyConnectedPageAction });

          dispatch(this.retrieveAllByCompany(companyId, pageNumber));
        }
        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  nullifyConnectedPage() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.nullifyConnectedPageAction });
    };
  }

  refreshAccessToken(pageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.refreshAccessTokenAction));
      this.clearErrorStatus(dispatch);
      try {
        const response = await ConnectedPageController.refreshAccessToken(
          pageId,
        );
        await dispatch(this.retrievePage(pageId));
        dispatch(this.unbusy());
        if (response) {
          dispatch(facebookErrorCRUDActions.retrieveAll(pageId));
        }
      } catch (e) {
        connectedPageHandler(dispatch, 401)(e);
        throw e;
      }
    };
  }

  retrievePage(
    pageId: number,
    withDowngradePending?: boolean,
    showInstagramAccounts?: boolean,
    skipBusy?: boolean,
    skipAudiencesReset?: boolean,
    withUnlayerHmac?: boolean,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      if (!skipBusy) {
        dispatch(this.busy(this.retrieveAction));
      }
      try {
        const state = getState();
        const companyId = selectActiveCompanyId(state);
        const response = await ConnectedPageController.retrievePage(
          pageId,
          companyId,
          withDowngradePending,
          showInstagramAccounts,
          withUnlayerHmac,
        );
        dispatch({
          type: this.fetchConnectedPageAction,
          page: response,
        });
        if (!skipAudiencesReset) {
          dispatch(audiencesCRUDActions.resetAudiences());
          dispatch(adAudienceCRUDActions.resetAdAudiences());
        }
        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  retrieveSubscriptionMessagingStatus(fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const state = getState();
      const isRealFacebookPage = hasConnectedRealFacebookPageSelector(state);

      if (!isRealFacebookPage) {
        return;
      }

      dispatch(this.busy(this.retrieveSubscriptionMessagingStatusAction));

      try {
        const response =
          await ConnectedPageController.retrieveSubscriptionMessagingStatus(
            fbPageId,
          );

        dispatch({
          type: this.retrievedSubscriptionMessagingStatusAction,
          status: response.status,
        });

        return response;
      } catch (e) {
        const res = await isInvitedUserAuthError(e);

        if (res) {
          dispatch(this.unbusy());
          return;
        }

        connectedPageHandler(dispatch)(e);
      }
    };
  }

  attachPagesTeam(companyId: number, subscriptionId: number, ids: number[]) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        const response = await ConnectedPageController.attachPagesTeam(
          subscriptionId,
          { ids },
        );
        dispatch(
          this.retrieveBasicListByCompany(
            companyId,
            {
              withSubscription: true,
              withDowngradePending: true,
              withMessagesStatistic: true,
            },
            true,
          ),
        );
        dispatch(subscriptionCRUDActions.getAccountData(companyId));

        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  fetchPagesForCompany(options: {
    companyId: number;
    pageNumber: number;
    perPage: number;
    searchQuery: string;
    withSubscription: boolean;
    withFacebookErrors: boolean;
    showInstagramAccounts: boolean;
    showActivated: boolean;
  }) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await ConnectedPageController.getAllByCompany(options);
        if (response) {
          dispatch({
            type: this.retrievedBasicListAction,
            basicPagesList: response.facebookPages,
          });
        }
        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  fetchAccessiblePagesByRole(roles?: number[]) {
    return async (dispatch: Dispatch<StoreState>) => {
      const defaultUserRoles = [
        UserRoleMappedToBECode.collaborator,
        UserRoleMappedToBECode.admin,
        UserRoleMappedToBECode.owner,
        UserRoleMappedToBECode.facebook_page_collaborator,
      ];
      try {
        const response =
          await ConnectedPageController.getAccessiblePagesByRoles(
            roles || defaultUserRoles,
          );
        if (response) {
          dispatch({
            type: this.fetchAccessiblePagesByRoleAction,
            accessiblePagesByRoles: response,
          });
        }
        return response;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  fetchCustomSquarePicture(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        return ConnectedPageController.fetchCustomSquarePicture(facebookPageId);
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }
  updateCustomSquarePicture(facebookPageId: number, assetId: number) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        const result = await ConnectedPageController.updateCustomSquarePicture(
          facebookPageId,
          assetId,
        );
        const activeFacebookPage = activeFacebookPageSelector(getState());
        if (activeFacebookPage && facebookPageId === activeFacebookPage.id) {
          dispatch(this.retrievePage(facebookPageId));
        }
        return result;
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }
  removeCustomSquarePicture(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        return ConnectedPageController.removeCustomSquarePicture(
          facebookPageId,
        );
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }

  fetchFollowUpConversations(companyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        return ConnectedPageController.fetchFollowUpConversations(companyId);
      } catch (e) {
        connectedPageHandler(dispatch)(e);
      }
    };
  }
}

const connectedPageCRUDActions = new ConnectedPageCRUDActions();

export const useConnectedPageCRUDActions = getActionHookBuilder(
  connectedPageCRUDActions,
  [
    'clearErrorStatus',
    'create',
    'retrieveAll',
    'retrieveAllByCompany',
    'retrieveBasicListByCompany',
    'retrieveAvailablePages',
    'clearAvailablePages',
    'addPagesToBackend',
    'allowPageForUsers',
    'moveToCompany',
    'update',
    'delete',
    'nullifyConnectedPage',
    'refreshAccessToken',
    'retrievePage',
    'retrieveSubscriptionMessagingStatus',
    'attachPagesTeam',
    'fetchPagesForCompany',
    'fetchCustomSquarePicture',
    'updateCustomSquarePicture',
    'removeCustomSquarePicture',
    'fetchFollowUpConversations',
    'createBusinessPage',
    'fetchAccessiblePagesByRole',
  ],
);

export default connectedPageCRUDActions;
